import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { productApi } from '@/services/productApi';

interface ProductDetailModalProps {
  product: any;
  isOpen: boolean;
  onClose: () => void;
  /** Maliyet fiyatı düzenleme yetkisi (örn. sipariş henüz teslim edilmediyse true) */
  allowCostEdit?: boolean;
}

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product,
  isOpen,
  onClose,
  allowCostEdit = false,
}) => {
  const productId: string | undefined = product?.id || product?.productId;
  const [costPrice, setCostPrice] = useState<string>('');
  const [originalCost, setOriginalCost] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !allowCostEdit || !productId) {
      setCostPrice('');
      setOriginalCost(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const p = await productApi.getProduct(productId);
        if (cancelled) return;
        const cp = typeof p.costPrice === 'number' ? p.costPrice : 0;
        setOriginalCost(cp);
        setCostPrice(String(cp));
      } catch (e: any) {
        if (!cancelled) toast.error(e.message || 'Maliyet alınamadı');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, allowCostEdit, productId]);

  const handleSaveCost = async () => {
    if (!productId) return;
    const value = Number(costPrice);
    if (Number.isNaN(value) || value < 0) {
      toast.error('Geçerli bir maliyet girin');
      return;
    }
    setSaving(true);
    try {
      await productApi.updateProduct(productId, { costPrice: value } as any);
      setOriginalCost(value);
      toast.success('Maliyet güncellendi');
    } catch (e: any) {
      toast.error(e.message || 'Maliyet güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !product) return null;

  const costChanged = originalCost !== null && Number(costPrice) !== originalCost;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <h2 className="text-2xl font-bold text-dark dark:text-light">Ürün Detayları</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {product.imageUrl && (
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Ürün Görseli</p>
              <img
                src={product.imageUrl}
                alt={product.title}
                className="w-full max-h-64 object-cover rounded-lg"
              />
            </div>
          )}

          <div>
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Ürün Adı</p>
            <p className="text-2xl font-bold text-dark dark:text-light">{product.title}</p>
          </div>

          {product.description && (
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Açıklama</p>
              <p className="text-gray-700 dark:text-gray-300">{product.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Birim Fiyatı</p>
              <p className="text-xl font-bold text-primary">₺{product.campaignPrice.toFixed(2)}</p>
              {product.campaignPrice < product.basePrice && (
                <p className="text-sm text-gray-500 line-through">₺{product.basePrice.toFixed(2)}</p>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Miktar</p>
              <p className="text-xl font-bold">{product.quantity}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Teslimat Tarihi</p>
              <p className="font-semibold text-dark dark:text-light">
                {new Date(product.deliveryDate).toLocaleDateString('tr-TR')}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Teslimat Saati</p>
              <p className="font-semibold text-dark dark:text-light">{product.deliveryTime}</p>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">Toplam Tutar</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              ₺{(product.campaignPrice * product.quantity).toFixed(2)}
            </p>
          </div>

          {allowCostEdit && productId && (
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
                Maliyet Fiyatı (costPrice)
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                Bu fiyat ürünün ana kaydında güncellenir. Sipariş teslim edildiğinde muhasebe hesaplamasında kullanılır.
              </p>
              {loading ? (
                <p className="text-sm text-gray-500">Yükleniyor...</p>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-300">₺</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={costPrice}
                    onChange={(e) => setCostPrice(e.target.value)}
                    disabled={saving}
                    className="flex-1 px-3 py-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 text-dark dark:text-light focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <button
                    onClick={handleSaveCost}
                    disabled={saving || !costChanged}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold disabled:opacity-50 hover:bg-amber-700 transition-colors"
                  >
                    {saving ? 'Kaydediliyor...' : 'Güncelle'}
                  </button>
                </div>
              )}
              {originalCost !== null && !loading && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Mevcut kayıt: ₺{originalCost.toFixed(2)}
                </p>
              )}
            </div>
          )}

          {product.category && (
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Kategori</p>
              <p className="text-dark dark:text-light">{product.category}</p>
            </div>
          )}

          {product.sku && (
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">SKU</p>
              <p className="text-dark dark:text-light font-mono">{product.sku}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
